import eventBus from './event-bus';
import { getAppEnvironment, getLogoUrl, getLogoColor, getActualHostEnvironment } from './envHelper';

export default {
    computed: {
        isSigningIn() {
            return this.$store.state.isSigningIn;
        },
        isExitingDemo() {
            return !!this.$store.state.isExitingDemo;
        },
        isAuthenticated() {
            return this.$store.state.isAuthenticated;
        },
        hasConnectedPlatform() {
            return this.$store.state.hasConnectedPlatform || this.$store.state.hasToken;
        },
        hasConfiguredProjectSpace() {
            return this.$store.state.hasConfiguredProjectSpace || this.$store.state.hasConfiguredProject || this.$store.state.hasConfig;
        },
        hasConfiguredProject() {
            return this.hasConfiguredProjectSpace;
        },
        hasBacklogSetup() {
            return !!this.$store.state.hasBacklogSetup;
        },
        hasRepoSetup() {
            return !!this.$store.state.hasRepoSetup;
        },
        configuredProjectSpaces() {
            const raw = this.$store.state.configuredProjectSpaces?.length
                ? this.$store.state.configuredProjectSpaces
                : (this.$store.state.configuredProjects || []);
            return (raw || []).filter(p => !p.isArchived);
        },
        configuredProjects() {
            return this.configuredProjectSpaces;
        },
        globalProjectSpaceId() {
            return this.$store.state.selectedProjectSpaceId || this.$store.state.selectedProjectId || localStorage.getItem('velocytrack_last_project_id');
        },
        globalProjectId() {
            return this.globalProjectSpaceId;
        },
        globalProjectSpace() {
            const id = this.globalProjectSpaceId;
            const spaces = this.configuredProjectSpaces;
            if (!spaces || !spaces.length) return null;
            if (!id) {
                return spaces.find(p => p.isPrimary) || spaces[0] || null;
            }
            const normId = String(id).toLowerCase();
            return spaces.find(p => String(p.id).toLowerCase() === normId || p.id === parseInt(id, 10))
                || spaces.find(p => p.isPrimary)
                || spaces[0]
                || null;
        },
        globalProject() {
            return this.globalProjectSpace;
        },
        currentProjectHasBacklog() {
            const p = this.globalProjectSpace;
            return !!p && !!p.backlogLinkId && !!p.backlogProjectId;
        },
        currentProjectHasRepo() {
            const p = this.globalProjectSpace;
            return !!p && !!p.repoLinkId;
        },
        // Deprecated aliases kept for backward compatibility
        hasToken() {
            return this.hasConnectedPlatform;
        },
        hasConfig() {
            return this.hasConfiguredProject;
        },
        effectiveEmployeeRole() {
            const p = this.globalProjectSpace;
            if (p && p.employeeRoleOverride !== null && p.employeeRoleOverride !== undefined) {
                return this.normalizeRole(p.employeeRoleOverride);
            }
            return this.normalizeRole(this.$store.state.employeeRole ?? 0);
        },
        isRoleDev() {
            return this.effectiveEmployeeRole === 0;
        },
        isRoleQA() {
            return this.effectiveEmployeeRole === 1;
        },
        isRoleSA() {
            return this.effectiveEmployeeRole === 2;
        },
        account() {
            return this.$store.state.account;
        },
        workspace() {
            return this.$store.state.workspace;
        },
        currentWorkspace() {
            return this.$store.state.workspace;
        },
        isActualDevHost() {
            return getActualHostEnvironment() === 'development';
        },
        currentEnvironment() {
            const actual = getActualHostEnvironment();
            if (actual !== 'development') return actual;
            const simEnv = this.$store.state.devSimulator?.envOverride;
            return getAppEnvironment(simEnv);
        },
        logoUrl() {
            return getLogoUrl(this.currentEnvironment);
        },
        logoColor() {
            return getLogoColor(this.currentEnvironment);
        },
        workspaces() {
            return this.$store.state.workspaces || [];
        },
        hasConfiguredTeamSpace() {
            const list = (this.$store.state.workspaces || []).filter(
                w => w && w.isTeamSpace !== false && (w.inviteToken || w.isTeamSpace === true)
            );
            return list.length > 0;
        },
        pendingTeamInvitations() {
            return this.$store.state.pendingTeamInvitations || [];
        },
        activeTeamNudges() {
            return this.$store.state.activeTeamNudges || [];
        },
        isDevEnv() {
            return this.currentEnvironment === 'development';
        },
        hasPrivilegeBypass() {
            return false;
        },
        planTier() {
            const ws = this.$store.state.workspace;
            if (!ws) return 0;
            if (typeof ws.plan === 'number') return ws.plan;
            const map = { Free: 0, Solo: 1, SmallTeam: 2, LargeTeam: 2, Team: 2, Business: 3, Enterprise: 4 };
            return map[ws.plan] ?? 0;
        },
        isSoloOrHigher() {
            if (this.hasPrivilegeBypass) return true;
            return !this.isViewOnlyMode;
        },
        isTeamOrHigher() {
            if (this.hasPrivilegeBypass) return true;
            const ws = this.$store.state.workspace;
            if (ws && (ws.isTrialing || this.planTier >= 2)) return true;
            const allWs = this.$store.state.workspaces || [];
            if (allWs.some(w => w && (w.isTrialing || w.plan === 2 || w.plan === 'Team' || w.plan === 'SmallTeam' || w.plan === 'LargeTeam'))) {
                return true;
            }
            if (this.globalProjectSpace && this.globalProjectSpace.sharedWorkspaceId) {
                return true;
            }
            return false;
        },
        purchasedSeatSlots() {
            const ws = this.$store.state.workspace;
            if (ws && typeof ws.purchasedSeatSlots === 'number') {
                return ws.purchasedSeatSlots;
            }
            if (this.isBetaUser) return 1;
            if (this.planTier >= 2) {
                return Math.max(1, ws?.seatSlots || 1);
            }
            return 0;
        },
        allocatedSeatSlots() {
            const ws = this.$store.state.workspace;
            if (ws && typeof ws.allocatedSeatSlots === 'number') {
                return ws.allocatedSeatSlots;
            }
            const ownedTeams = (this.$store.state.workspaces || []).filter(
                w => w && (w.currentUserRole === 0 || w.currentUserRole === 'Owner')
            );
            return ownedTeams.reduce((sum, w) => sum + Math.max(1, w.seatSlots || 1), 0);
        },
        availableSeatSlots() {
            const ws = this.$store.state.workspace;
            if (ws && typeof ws.availableSeatSlots === 'number') {
                return ws.availableSeatSlots;
            }
            return Math.max(0, this.purchasedSeatSlots - this.allocatedSeatSlots);
        },
        isBusinessOrTrial() {
            if (this.hasPrivilegeBypass) return true;
            const ws = this.$store.state.workspace;
            if (!ws) return false;
            if (ws.isTrialing) return true;
            return this.planTier >= 3;
        },
        canAddMultiplePlatforms() {
            return this.hasPrivilegeBypass || this.isSoloOrHigher;
        },
        canAddMultipleOrganizations() {
            return this.hasPrivilegeBypass || this.isSoloOrHigher;
        },
        canAddMultipleProjects() {
            return this.hasPrivilegeBypass || this.isSoloOrHigher;
        },
        canAddPlatformConnection() {
            if (this.hasPrivilegeBypass) return true;
            if (this.workspace && typeof this.workspace.canAddPlatformConnection === 'boolean') {
                return this.workspace.canAddPlatformConnection;
            }
            const count = (this.$store.state.platformLinks || []).length;
            const ws = this.workspace;
            if (ws && typeof ws.maxPlatformConnections === 'number') {
                return count < ws.maxPlatformConnections;
            }
            if (this.planTier === 0 && (!this.workspace || !this.workspace.isTrialing)) return count < 1;
            if (this.planTier >= 1) return count < 100;
            return true;
        },
        canAddProjectSpace() {
            if (this.hasPrivilegeBypass) return true;
            if (this.workspace && typeof this.workspace.canAddProjectSpace === 'boolean') {
                return this.workspace.canAddProjectSpace;
            }
            const count = (this.configuredProjectSpaces || []).length;
            const ws = this.workspace;
            if (ws && typeof ws.maxProjectSpaces === 'number') {
                return count < ws.maxProjectSpaces;
            }
            if (this.planTier === 0 && (!this.workspace || !this.workspace.isTrialing)) return count < 1;
            if (this.planTier >= 1) return count < 100;
            return true;
        },
        canAddProject() {
            return this.canAddProjectSpace;
        },
        dailyMutationLimit() {
            if (this.hasPrivilegeBypass) return null;
            const p = this.globalProjectSpace;
            if (p && (p.isSharedProjectSpace || p.sharedWorkspaceId)) return null;
            if (p && typeof p.dailyMutationLimit === 'number') return p.dailyMutationLimit;
            const ws = this.workspace;
            if (ws && ws.isTrialing) return null;
            if (ws && typeof ws.dailyMutationLimit === 'number' && this.planTier === 0) return ws.dailyMutationLimit;
            if (this.planTier === 0) return 5;
            return null;
        },
        dailyMutationsRemaining() {
            const limit = this.dailyMutationLimit;
            if (limit === null) return null;
            const p = this.globalProjectSpace;
            if (p && typeof p.dailyMutationsRemaining === 'number') return p.dailyMutationsRemaining;
            const ws = this.workspace;
            if (ws && typeof ws.dailyMutationsRemaining === 'number') return ws.dailyMutationsRemaining;
            return limit;
        },
        hasFreeDailyQuota() {
            return typeof this.dailyMutationLimit === 'number' && this.dailyMutationLimit > 0;
        },
        isViewOnlyMode() {
            if (this.hasPrivilegeBypass) return false;
            if (this.globalProjectSpace && typeof this.globalProjectSpace.isViewOnly === 'boolean') {
                return this.globalProjectSpace.isViewOnly;
            }
            if (this.workspace && typeof this.workspace.isViewOnly === 'boolean') {
                return this.workspace.isViewOnly;
            }
            const ws = this.$store.state.workspace;
            if (!ws) return false;
            if (ws.isTrialing) return false;
            if (this.planTier === 0) {
                return typeof this.dailyMutationsRemaining === 'number' ? this.dailyMutationsRemaining <= 0 : false;
            }
            return false;
        },
        isPersonalViewOnlyMode() {
            if (this.hasPrivilegeBypass) return false;
            const ws = this.workspace;
            if (ws && typeof ws.isPersonalViewOnly === 'boolean') {
                return ws.isPersonalViewOnly;
            }
            if (ws && typeof ws.isViewOnly === 'boolean') {
                return ws.isViewOnly;
            }
            if (this.planTier === 0 && (!ws || !ws.isTrialing)) {
                const rem = (ws && typeof ws.dailyMutationsRemaining === 'number') ? ws.dailyMutationsRemaining : 5;
                return rem <= 0;
            }
            return false;
        },
        userTheme() {
            return this.$store.state.theme || 'dark';
        },
        userCountryCode() {
            return this.$store.state.countryCode || 'BG';
        },
        userLocale() {
            return this.$store.state.locale || 'en-US';
        },
        userTimezone() {
            return this.$store.state.timezone || 'UTC';
        },
        isBetaUser() {
            if (!this.isAuthenticated) return false;
            if (this.isActualDevHost) {
                return this.currentEnvironment === 'beta';
            }
            if (this.currentEnvironment === 'production') return false;
            if (this.currentEnvironment === 'beta') return true;
            return Boolean(this.$store.state.isBetaUser || this.workspace?.isBetaUser);
        },
        isDemo() {
            if (this.account?.isDemoAccount === true) return true;
            const email = (this.account?.email || '').toLowerCase();
            return email.includes('@demo');
        },
        isDemoMode() {
            return this.isDemo;
        },
        hasAcknowledgedBetaWelcome() {
            if (this.isActualDevHost || this.isDemo) return true;
            return !!this.$store.state.hasAcknowledgedBetaWelcome;
        },
        daysRemainingInTrial() {
            const ws = this.$store.state.workspace;
            if (!ws || !ws.trialEndsAt) return 0;
            const diff = new Date(ws.trialEndsAt) - new Date();
            return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
        }
    },
    methods: {
        async loadTeamSessionDataAsync() {
            if (!this.isAuthenticated || !this.isDevEnv) return;
            try {
                const [wsListResp, invitesResp, nudgesResp] = await Promise.all([
                    this.$http.get('/Workspace/List').catch(() => ({ data: [] })),
                    this.$http.get('/Workspace/Invitations/Pending').catch(() => ({ data: [] })),
                    this.$http.get('/TeamReport/Nudges/Active').catch(() => ({ data: [] }))
                ]);
                this.$store.commit('setWorkspaces', wsListResp.data || []);
                this.$store.commit('setPendingTeamInvitations', invitesResp.data || []);
                this.$store.commit('setActiveTeamNudges', nudgesResp.data || []);
            } catch (e) {
                console.warn('Could not load team session data:', e);
            }
        },
        async initSessionConfigAsync() {
            if (!this.isAuthenticated) {
                if (typeof window !== 'undefined') window.__vtSessionConfigReady = true;
                return false;
            }
            if (typeof window !== 'undefined') window.__vtSessionConfigReady = false;

            try {
                // Check beta user status
                let isBeta = false;
                let hasAck = false;
                try {
                    const betaResp = await this.$http.get("/AccountConfig/BetaUserStatus");
                    hasAck = !!betaResp.data?.hasAcknowledgedBetaWelcome;
                    isBeta = !!betaResp.data?.isBetaUser || (!hasAck && !!this.account?.isBetaUser);
                    this.$store.commit('setBetaUser', isBeta);
                    this.$store.commit('setAcknowledgedBetaWelcome', hasAck);

                    if (isBeta && !hasAck && !this.isDemoMode) {
                        this.$nextTick(() => {
                            eventBus.$emit('show-beta-welcome-modal');
                        });
                    }
                } catch (bErr) {
                    this.$store.commit('setBetaUser', false);
                    this.$store.commit('setAcknowledgedBetaWelcome', false);
                }

                const platformResp = await this.$http.get("/AccountConfig/HasConnectedPlatform");
                const hasConnected = !!platformResp.data;
                this.$store.commit('hasConnectedPlatform', hasConnected);

                // Load Workspace only if consent has been acknowledged or not beta user
                if (!isBeta || hasAck) {
                    try {
                        const wsResp = await this.$http.get("/Workspace");
                        this.$store.commit('setWorkspace', wsResp.data || null);
                    } catch (wsErr) {
                        console.warn("Could not fetch workspace:", wsErr);
                    }
                    await this.loadTeamSessionDataAsync();
                } else {
                    this.$store.commit('setWorkspace', null);
                    this.$store.commit('setWorkspaces', []);
                }

                if (hasConnected) {
                    const [configResp, roleResp, projectsResp] = await Promise.all([
                        this.$http.get("/AccountConfig/HasConfiguredProject"),
                        this.$http.get("/AccountConfig/EmployeeRole"),
                        this.$http.get("/Config/ProjectSpaces").catch(() => this.$http.get("/Config/Projects")).catch(() => ({ data: [] }))
                    ]);
                    this.$store.commit('hasConfiguredProjectSpace', !!configResp.data);
                    this.$store.commit('setEmployeeRoleId', roleResp.data);
                    const spaces = projectsResp.data || [];
                    this.$store.commit('setConfiguredProjectSpaces', spaces);

                    const activeSpaces = spaces.filter(p => !p.isArchived);
                    if (activeSpaces.length > 0) {
                        const currentSelectedId = this.$store.state.selectedProjectSpaceId || this.$store.state.selectedProjectId || localStorage.getItem('velocytrack_last_project_id');
                        const normCurrentId = currentSelectedId ? String(currentSelectedId).toLowerCase() : null;
                        const currentStillValid = normCurrentId
                            ? activeSpaces.find(p => String(p.id).toLowerCase() === normCurrentId || p.id === parseInt(currentSelectedId, 10))
                            : null;
                        const activeToSelect = currentStillValid || activeSpaces.find(p => p.isPrimary) || activeSpaces[0];
                        if (activeToSelect) {
                            this.$store.commit('setSelectedProjectSpaceId', activeToSelect.id);
                            this.$store.commit('setSelectedProjectId', activeToSelect.id);
                            try {
                                localStorage.setItem('velocytrack_last_project_id', activeToSelect.id);
                            } catch (e) {
                                // ignore
                            }
                            eventBus.$emit('global-project-changed', activeToSelect.id);
                        }
                    } else {
                        this.$store.commit('clearSelectedProjectSpaceId');
                        this.$store.commit('clearSelectedProjectId');
                        try {
                            localStorage.removeItem('velocytrack_last_project_id');
                        } catch (e) {
                            // ignore
                        }
                        eventBus.$emit('global-project-changed', null);
                    }
                    return true;
                } else {
                    let spaces = [];
                    if (this.isDevEnv) {
                        // Even without a personal platform connection yet, check if the user has shared Team Project Spaces
                        try {
                            const projectsResp = await this.$http.get("/Config/ProjectSpaces").catch(() => ({ data: [] }));
                            spaces = projectsResp.data || [];
                        } catch (pErr) {
                            spaces = [];
                        }
                    }
                    this.$store.commit('setConfiguredProjectSpaces', spaces);
                    this.$store.commit('hasConfiguredProjectSpace', spaces.length > 0);
                    if (spaces.length === 0) {
                        this.$store.commit('clearSelectedProjectSpaceId');
                        this.$store.commit('clearSelectedProjectId');
                        try {
                            localStorage.removeItem('velocytrack_last_project_id');
                        } catch (e) {
                            // ignore
                        }
                        eventBus.$emit('global-project-changed', null);
                    }
                    return false;
                }
            } catch (err) {
                console.error("Failed to load session configuration", err);
                return false;
            } finally {
                if (typeof window !== 'undefined') window.__vtSessionConfigReady = true;
            }
        },
        normalizeRole(role) {
            if (role === null || role === undefined) return 0;
            if (role === 0 || role === '0' || role === 'Developer') return 0;
            if (role === 1 || role === '1' || role === 'QA') return 1;
            if (role === 2 || role === '2' || role === 'SystemAnalyst' || role === 'System Analyst') return 2;
            const parsed = parseInt(role, 10);
            return isNaN(parsed) ? 0 : parsed;
        },
        roleName(role) {
            const num = this.normalizeRole(role);
            switch (num) {
                case 0: return 'Developer';
                case 1: return 'QA';
                case 2: return 'System Analyst';
                default: return 'Developer';
            }
        },
        normalizePlatformType(type) {
            if (type === 0 || type === '0' || type === 'AzureDevOps') return 0;
            if (type === 1 || type === '1' || type === 'Jira') return 1;
            if (type === 2 || type === '2' || type === 'GitHub') return 2;
            if (type === 3 || type === '3' || type === 'GitLab') return 3;
            return 0;
        },
        platformBadgeVariant(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'primary';
                case 1: return 'info';
                case 2: return 'dark';
                case 3: return 'warning';
                default: return 'primary';
            }
        },
        platformIconOnly(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'icon-azure';
                case 1: return 'fab fa-jira';
                case 2: return 'fab fa-github';
                case 3: return 'fab fa-gitlab';
                default: return 'fas fa-cloud';
            }
        },
        platformTypeName(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'Azure DevOps';
                case 1: return 'Jira';
                case 2: return 'GitHub';
                case 3: return 'GitLab';
                default: return 'Azure DevOps';
            }
        },
        platformBrandClass(type) {
            const t = this.normalizePlatformType(type);
            switch (t) {
                case 0: return 'brand-azure';
                case 1: return 'brand-jira';
                case 2: return 'brand-github';
                case 3: return 'brand-gitlab';
                default: return 'brand-default';
            }
        },
        getProjectPlatforms(p) {
            if (!p) return [];
            const platforms = [];
            const addPlatform = (type) => {
                if (type === null || type === undefined) return;
                const norm = this.normalizePlatformType(type);
                if (!platforms.some(pl => pl.type === norm)) {
                    platforms.push({
                        type: norm,
                        icon: this.platformIconOnly(norm),
                        variant: this.platformBadgeVariant(norm),
                        name: this.platformTypeName(norm),
                        brandClass: this.platformBrandClass(norm)
                    });
                }
            };
            if (p.backlogLinkId && p.backlogProjectId) {
                addPlatform(p.backlogPlatformType);
            }
            if (p.repoLinkId) {
                addPlatform(p.repoPlatformType);
            }
            return platforms;
        },
        formatProjectPlatformTags(p) {
            if (!p) return '';
            const tags = [];
            const platShort = (t) => {
                const norm = this.normalizePlatformType(t);
                switch (norm) {
                    case 0: return 'AZ';
                    case 1: return 'Jira';
                    case 2: return 'GitHub';
                    case 3: return 'GitLab';
                    default: return '';
                }
            };
            if (p.backlogLinkId && p.backlogProjectId) {
                const tag = platShort(p.backlogPlatformType);
                if (tag && !tags.includes(tag)) tags.push(tag);
            }
            if (p.repoLinkId) {
                const tag = platShort(p.repoPlatformType);
                if (tag && !tags.includes(tag)) tags.push(tag);
            }
            return tags.length ? ` (${tags.join(' + ')})` : '';
        }
    }
};
